import androidx.compose.runtime.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.renderComposable
import data.FoodSafetyEntry
import data.FoodSafetySummary
import data.loadFoodsFromCsv
import data.loadSynonymsFromCsv
import org.jetbrains.compose.web.attributes.InputType
import ui.SourceListTab
import ui.VerticalVerdictTabs
import ui.TriGroupRecommendationRow
import ui.buildReliabilityAwareSummary
import ui.verdictLabel
import kotlin.math.max
import org.w3c.dom.HTMLInputElement
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay

import org.jetbrains.compose.web.css.Style
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import ui.AppStyles



// -------------------
// GA4 연동 유틸리티
// -------------------
external fun gtag(command: String, eventName: String, parameters: dynamic)

private fun logSearchEvent(searchTerm: String) {
    if (searchTerm.isBlank()) return
    val params = js("{}")
    params["search_term"] = searchTerm

    try {
        gtag("event", "search", params)
        println("GA4 Log: Search term sent -> $searchTerm")
    } catch (e: Throwable) {
        println("GA4 Error: ${e.message}")
    }
}

// -------------------
// 정규화/유틸
// -------------------
private fun norm(s: String) = s.lowercase().replace(Regex("\\s+"), "")

private fun tokensBySpace(s: String): List<String> =
    s.trim().split(Regex("\\s+")).filter { it.isNotBlank() }

private fun isHangul(c: Char): Boolean = c in '\uAC00'..'\uD7A3'

private fun trailingHangulRunLen(haystack: String, needle: String): Int {
    val h = haystack.lowercase()
    val n = needle.lowercase()
    val idx = h.indexOf(n)
    if (idx < 0) return 0
    var j = idx + n.length
    var cnt = 0
    while (j < h.length && isHangul(h[j])) {
        cnt++
        j++
    }
    return cnt
}

private fun containsHangulBoundary(haystack: String, needle: String): Boolean {
    if (needle.isBlank()) return false
    val h = haystack.lowercase()
    val n = needle.lowercase()

    var idx = h.indexOf(n)
    while (idx >= 0) {
        val beforeOk = (idx == 0) || !isHangul(h[idx - 1])
        val afterIdx = idx + n.length
        val afterOk = (afterIdx >= h.length) || !isHangul(h[afterIdx])
        if (beforeOk && afterOk) return true
        idx = h.indexOf(n, startIndex = idx + 1)
    }
    return false
}

private fun relevance(name: String, query: String, datasetScore: Double): Double {
    val n = norm(name)
    val q = norm(query)
    if (n == q) return 1000.0 + datasetScore
    var score = 0.0
    if (n.startsWith(q)) score += 200.0
    if (n.contains(q)) score += 120.0

    val nt = tokensBySpace(name).map { it.lowercase() }.toSet()
    val qt = tokensBySpace(query).map { it.lowercase() }.toSet()
    if (qt.isNotEmpty()) {
        val inter = nt.intersect(qt).size.toDouble()
        val ratio = inter / max(1, qt.size)
        score += 100.0 * ratio
    }

    val extraLen = (n.length - q.length).coerceAtLeast(0)
    score += 80.0 / (1 + extraLen)
    score += datasetScore * 10.0
    return score
}

private fun norm2(s: String) = s.lowercase().replace(Regex("\\s+"), "")

private fun expandQuery(q: String, synonyms: Map<String, List<String>>): List<String> {
    val base = q.trim()
    if (base.isBlank()) return emptyList()
    val n = norm2(base)

    return buildList {
        add(base)
        if (n != base) add(n)
        synonyms[base]?.let { addAll(it) }
        synonyms[n]?.let { addAll(it) }
    }
        .map { it.trim() }
        .filter { it.isNotBlank() }
        .distinct()
}

private fun buildMeaningfulTokens2to4FromAllNames(summaries: List<FoodSafetySummary>): Set<String> {
    val out = mutableSetOf<String>()
    summaries.asSequence()
        .map { norm(it.name.trim()) }
        .filter { it.isNotBlank() }
        .forEach { n ->
            val L = n.length
            for (len in 2..4) {
                if (L < len) continue
                for (i in 0..(L - len)) {
                    val sub = n.substring(i, i + len)
                    out.add(sub)
                }
            }
        }
    return out
}

private fun buildMeaningfulWhole1gFromDb(summaries: List<FoodSafetySummary>): Set<String> =
    summaries
        .map { it.name.trim() }
        .filter { it.length == 1 && it.isNotBlank() }
        .map { norm(it) }
        .toSet()

private fun extractHintsFromDbTokens(
    query: String,
    meaningful2to4: Set<String>,
    meaningfulWhole1g: Set<String>
): List<String> {
    val q = norm(query)
    if (q.isBlank()) return emptyList()

    val found2to4 = mutableSetOf<String>()
    val L = q.length
    for (len in 2..4) {
        if (L < len) continue
        for (i in 0..(L - len)) {
            val sub = q.substring(i, i + len)
            if (sub in meaningful2to4) found2to4.add(sub)
        }
    }

    val found1 = mutableSetOf<String>()
    for (ch in q) {
        val s = ch.toString()
        if (s in meaningfulWhole1g) found1.add(s)
    }

    val filtered1 = if (found2to4.isNotEmpty()) {
        found1.filterNot { one ->
            found2to4.any { it.contains(one) }
        }.toSet()
    } else {
        found1
    }

    val combined = (found2to4 + filtered1)
    if (combined.isEmpty()) return emptyList()

    return combined.sortedWith(
        compareBy<String> { q.indexOf(it) }.thenByDescending { it.length }
    )
}

private data class RankKey( val bucket: Int, val score: Double, val nameLenDesc: Int)

private fun rankKey(
    s: FoodSafetySummary,
    query: String,
    expanded: List<String>,
    orderedHints: List<String>
): RankKey {
    val name = s.name
    val nName = norm(name)
    val nQuery = norm(query)

    if (nName == nQuery) {
        return RankKey(bucket = 0, score = 1_000_000.0 + s.scoreRounded, nameLenDesc = -name.length)
    }

    if (orderedHints.isNotEmpty()) {
        val idx = orderedHints.indexOfFirst { h -> norm(h) == nName }
        if (idx >= 0) {
            val hint = orderedHints[idx]
            val bonus = (10_000 - idx * 500) + hint.length * 50
            return RankKey(bucket = 1, score = 500_000.0 + bonus + s.scoreRounded, nameLenDesc = -name.length)
        }
    }

    if (orderedHints.isNotEmpty()) {
        val hitInfos = orderedHints.mapNotNull { h ->
            if (nName.contains(norm(h))) {
                val pos = nName.indexOf(norm(h))
                Triple(h, pos, h.length)
            } else null
        }

        if (hitInfos.isNotEmpty()) {
            val bestHint = hitInfos.minWith(compareBy<Triple<String, Int, Int>> {
                orderedHints.indexOf(it.first)
            }.thenBy { it.second })

            val hintOrder = orderedHints.indexOf(bestHint.first).coerceAtLeast(0)
            val hintLen = bestHint.third
            val boundaryBonus = if (containsHangulBoundary(name, bestHint.first)) 2_000.0 else 0.0
            val bonus = (5_000 - hintOrder * 300) + hintLen * 200 + boundaryBonus
            return RankKey(bucket = 2, score = 200_000.0 + bonus + relevance(name, query, s.scoreRounded.toDouble()), nameLenDesc = -name.length)
        }
    }

    val expOnly = expanded.filter { norm2(it) != nQuery }
    if (expOnly.any { containsHangulBoundary(name, it) }) {
        return RankKey(bucket = 3, score = 80_000.0 + relevance(name, query, s.scoreRounded.toDouble()), nameLenDesc = -name.length)
    }

    if (name.contains(query.trim(), ignoreCase = true)) {
        val tail = trailingHangulRunLen(name, query.trim())
        val penalty = 120.0 * tail
        return RankKey(bucket = 4, score = 10_000.0 + relevance(name, query, s.scoreRounded.toDouble()) - penalty, nameLenDesc = -name.length)
    }

    return RankKey(bucket = 9, score = relevance(name, query, s.scoreRounded.toDouble()), nameLenDesc = -name.length)
}

private fun verdictRank(v: String?): Int = when (v?.lowercase()) {
    "avoid" -> 3
    "caution" -> 2
    "conditional" -> 1
    "safe" -> 0
    else -> -1
}

// -------------------
// Entry
// -------------------
fun main() {
    renderComposable(rootElementId = "root") {
        App()
    }
}

@Composable
fun App() {
    Style(AppStyles)   // UI style

    var query by remember { mutableStateOf("") }
    var summaries by remember { mutableStateOf<List<FoodSafetySummary>>(emptyList()) }
    var synonyms by remember { mutableStateOf<Map<String, List<String>>>(emptyMap()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    // CSV 로딩 (1회)
    LaunchedEffect(Unit) {
        try {
            summaries = loadFoodsFromCsv("foods.csv")
            synonyms = loadSynonymsFromCsv("synonyms.csv")
            error = null
        } catch (e: Throwable) {
            error = e.message ?: "데이터 로드 실패"
        } finally {
            loading = false
        }
    }

    // 검색어 GA 디 바운싱 (0.4초)
    LaunchedEffect(query) {
        val trimmedQuery = query.trim()
        if (trimmedQuery.length >= 2) {
            delay(400)
            logSearchEvent(trimmedQuery)
        }
    }

    val meaningful2to4 =
        remember(summaries) { buildMeaningfulTokens2to4FromAllNames(summaries) }
    val meaningfulWhole1g = remember(summaries) { buildMeaningfulWhole1gFromDb(summaries) }

    val ranked = remember(query, summaries, synonyms, meaningful2to4, meaningfulWhole1g) {
        if (query.isBlank()) emptyList()
        else {
            val expanded = expandQuery(query, synonyms)
            val orderedHints =
                extractHintsFromDbTokens(query, meaningful2to4, meaningfulWhole1g)

            val candidates = summaries.filter { s ->
                val nName = norm(s.name)
                val nQuery = norm(query)
                (nName == nQuery) ||
                        expanded.any { qx -> nName.contains(norm(qx)) } ||
                        orderedHints.any { h -> nName.contains(norm(h)) }
            }

            candidates.sortedWith(
                compareBy<FoodSafetySummary> { rankKey(it, query, expanded, orderedHints).bucket }
                    .thenByDescending { rankKey(it, query, expanded, orderedHints).score }
                    .thenBy { rankKey(it, query, expanded, orderedHints).nameLenDesc }
                    .thenByDescending { it.scoreRounded }
            )
        }
    }

    val visibleRanked = remember(ranked) { ranked.filter { it.entries.isNotEmpty() } }

    Div({ classes(AppStyles.container) }) {
        SearchHeaderCard(
            title = "맘마미: 임산부 음식 안심 검색",
            subtitle = "여러 출처의 의견을 신뢰도를 기준으로 정리해 보여드려요.",
            query = query,
            onChange = { query = it },
            onClear = { query = "" }
        )

        Div({classes(AppStyles.resultsPanel)  }) {
            when {
                loading -> P { Text("불러오는 중...") }
                error != null -> P({ style { color(Color.red) } }) { Text("오류: $error") }

                query.isNotBlank() && visibleRanked.isEmpty() ->
                    P({ style { color(Color.red) } }) { Text("검색 결과가 없습니다.") }

                query.isNotBlank() -> visibleRanked.forEach { summary ->
                    var selectedTab by remember(query, summary.name) { mutableStateOf("all") }

                    // 1. 개별 결과 카드 (AppStyles.resultCard 사용)
                    Div({ classes(AppStyles.resultCard) }) {
                        H2({
                            style {
                                marginTop(0.px)
                                color(Color("#1f2937"))
                                fontSize(24.px)
                            }
                        }) { Text("${summary.name}") }

                        // 요약 텍스트 렌더링 부분 (기존 로직 유지)
                        val summaryText = buildReliabilityAwareSummary(summary.entries).trim()
                        val entries = summary.entries
                        val total = entries.size.coerceAtLeast(1)
                        val avoid = entries.count { it.verdict.equals("avoid", true) }
                        val caution = entries.count { it.verdict.equals("caution", true) }
                        val conditional = entries.count { it.verdict.equals("conditional", true) }

                        fun pct(n: Int) = n * 100.0 / total
                        val (bgColor, fgColor) = when {
                            pct(avoid) >= 40.0 -> Pair("#fee2e2", "#b91c1c")
                            pct(caution) >= 40.0 -> Pair("#fef3c7", "#92400e")
                            pct(conditional) >= 40.0 -> Pair("#e0f2fe", "#075985")
                            else -> Pair("#dcfce7", "#166534")
                        }

                        if (summaryText.isNotBlank()) {
                            Div({
                                style {
                                    backgroundColor(Color(bgColor)); borderRadius(10.px)
                                    padding(14.px); marginTop(10.px); marginBottom(12.px)
                                    property("border", "1px solid rgba(0,0,0,.04)")
                                }
                            }) {
                                P({
                                    style {
                                        margin(0.px); color(Color(fgColor)); fontSize(15.px)
                                        lineHeight("1.5"); fontWeight("600")
                                    }
                                }) { Text(summaryText) }
                            }
                        }

                        // 3분할 신뢰도 그룹
                        TriGroupRecommendationRow(summary.entries)

                        // 탭 필터링 데이터 준비
                        val sortedEntries = remember(summary.entries) {
                            summary.entries.sortedWith(
                                compareByDescending<FoodSafetyEntry> { verdictRank(it.verdict) }
                                    .thenByDescending { it.reliability }
                            )
                        }
                        val filteredEntries = remember(selectedTab, sortedEntries) {
                            if (selectedTab == "all") sortedEntries
                            else sortedEntries.filter { it.verdict.lowercase() == selectedTab }
                        }

                        // 2. 탭 영역 (AppStyles.tabsArea 사용)
                        Div({ classes(AppStyles.tabsArea) }) {
                            // 왼쪽 탭 버튼
                            VerticalVerdictTabs(selectedTab) { tab -> selectedTab = tab }

                            // 오른쪽 리스트 내용 (AppStyles.tabContent 사용)
                            Div({ classes(AppStyles.tabContent) }) {
                                P({
                                    style {
                                        property("font-weight", "bold")
                                        color(Color("#374151"))
                                        marginBottom(8.px)
                                        // 긴 제목 방지
                                        property("white-space", "nowrap")
                                        property("overflow", "hidden")
                                        property("text-overflow", "ellipsis")
                                    }
                                }) {
                                    val label = if (selectedTab == "all") "전체 출처 목록"
                                    else "${verdictLabel(selectedTab)} 출처 목록"
                                    Text("🔍 $label (${filteredEntries.size}개)")
                                }

                                // 출처 리스트 출력
                                SourceListTab(filteredEntries)
                            }
                        }
                    }
                }

                else -> {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            flexWrap(FlexWrap.Wrap)
                            gap(20.px)
                            maxWidth(700.px)
                            width(100.percent)
                        }
                    }) {
                        Div({ style { textAlign("center") } }) {
                            P({
                                style {
                                    margin(0.px)
                                    marginBottom(6.px)
                                    color(Color("#0f172a"))
                                    fontSize(18.px)
                                    fontWeight("600")
                                }
                            }) { Text("이 음식, 먹어도 괜찮을까요?") }

                            val foodsCount = summaries
                                .map { it.name.trim() }
                                .distinct()
                                .size

                            val sourcesCount = summaries
                                .flatMap { it.entries }
                                .mapNotNull { it.url?.trim()?.lowercase() }
                                .distinct()
                                .size

                            P({
                                style {
                                    marginTop(10.px)
                                    margin(0.px)
                                    fontSize(13.px)
                                    color(Color("#64748b")) // 기본 문장 색
                                    textAlign("center")
                                }
                            }) {
                                Span({
                                    style {
                                        color(Color("#2563eb")) // 숫자 강조 색 (블루)
                                        fontWeight("600")
                                    }
                                }) {
                                    Text("${sourcesCount}개의 출처")
                                }

                                Text("에서 ")

                                Span({
                                    style {
                                        color(Color("#2563eb"))
                                        fontWeight("600")
                                    }
                                }) {
                                    Text("${foodsCount}개의 음식")
                                }

                                Text(" 자료를 모아 정리했어요.")
                            }
                        }

                        Img(src = "main.png", attrs = {
                            style { width(180.px); height(180.px) }
                        })
                    }

                }
            }
            IntroContentSection()
        }
    }

    DisposableEffect(Unit) {
        val handler: (Event) -> Unit = {
            val active = document.activeElement as? HTMLElement
            if (active?.id == "search-input") {
                (document.getElementById("search-input") as? HTMLInputElement)?.blur()
            }
        }

        window.addEventListener("scroll", handler)

        onDispose {
            window.removeEventListener("scroll", handler)
        }
    }
}
@Composable
private fun SearchHeaderCard(
    title: String,
    subtitle: String,
    query: String,
    onChange: (String) -> Unit,
    onClear: () -> Unit
) {
    Div({ classes(AppStyles.headerCard) }) {
        Div({ classes(AppStyles.headerRow) }) {
            Img(src = "logo.png", attrs = {
                classes(AppStyles.headerLogo)
            })

            Div({ classes(AppStyles.headerTexts) }) {
                H1({ classes(AppStyles.headerTitle) }) { Text(title) }
                Span({ classes(AppStyles.headerSubtitle) }) { Text(subtitle) }
            }
        }

        SearchBar(
            value = query,
            onChange = onChange,
            onClear = onClear,
            placeholder = "음식 이름을 입력하세요 (예: 회, 커피, 팥)"
        )

        P({ classes(AppStyles.disclaimerText) }) {
            Text("맘마미는 의료적 조언을 제공하지 않으며, 공개된 정보를 바탕으로 한 검색·요약 서비스입니다. 개인 건강 상태에 따라 의료 전문가와 상담하세요.")
        }
    }
}

@Composable
private fun SearchBar(
    value: String,
    onChange: (String) -> Unit,
    onClear: () -> Unit,
    placeholder: String
) {
    Div({
        style {
            position(Position.Relative)
            marginTop(4.px)
            marginBottom(6.px)
        }
    }) {
        Span({
            style {
                position(Position.Absolute)
                left(12.px); top(0.px); bottom(0.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                color(Color("#94a3b8"))
            }
        }) { Text("🔍") }

        Input(type = InputType.Text, attrs = {
            id("search-input")
            value(value)
            onInput { onChange(it.value) }
            attr("placeholder", placeholder)
            if (value.isBlank()) attr("autofocus", "true")

            style {
                width(100.percent)
                fontSize(16.px)
                padding(14.px, 44.px, 14.px, 40.px)
                borderRadius(14.px)
                border(1.px, LineStyle.Solid, Color("#cbd5e1"))
                backgroundColor(Color("#f8fafc"))
                outline("none")
                property("box-sizing", "border-box")
            }

            onFocus {
                val el = it.target as HTMLInputElement
                el.style.border = "1px solid #60a5fa"
                el.style.boxShadow = "0 0 0 4px rgba(96,165,250,.2)"
                el.style.backgroundColor = "#ffffff"
            }
            onBlur {
                val el = it.target as HTMLInputElement
                el.style.border = "1px solid #cbd5e1"
                el.style.boxShadow = ""
                el.style.backgroundColor = "#f8fafc"
            }
        })

        if (value.isNotBlank()) {
            Button({
                onClick {
                    onClear()
                    (document.getElementById("search-input") as? HTMLInputElement)?.focus()
                }
                style {
                    position(Position.Absolute)
                    right(8.px); top(0.px); bottom(0.px)
                    border(0.px); backgroundColor(Color.transparent); cursor("pointer")
                    padding(0.px, 10.px); color(Color("#94a3b8")); fontSize(18.px)
                }
            }) { Text("✕") }
        }
    }
}

@Composable
private fun IntroContentSection() {
    Div({
        style {
            maxWidth(760.px)
            width(100.percent)
            marginTop(22.px)
            padding(18.px)
            borderRadius(16.px)
            backgroundColor(Color("#ffffff"))
            border(1.px, LineStyle.Solid, Color("#e5e7eb"))
            property("box-shadow", "0 1px 8px rgba(15,23,42,.04)")
        }
    }) {
        H2({
            style {
                marginTop(0.px)
                marginBottom(10.px)
                fontSize(18.px)
                color(Color("#0f172a"))
            }
        }) { Text("맘마미는 어떤 서비스인가요?") }

        P({
            style {
                marginTop(0.px)
                marginBottom(12.px)
                fontSize(14.px)
                color(Color("#374151"))
                lineHeight("1.6")
            }
        }) {
            Text("임산부와 부모를 위해 여러 공개 출처의 식품 안전 정보를 모아 ")
            Text("한눈에 비교·정리해주는 검색·요약 서비스입니다. ")
            Text("같은 음식이라도 출처에 따라 서로 다른 의견이 존재할 수 있으며, ")
            Text("맘마미는 출처의 신뢰도를 3단계로 구분하여 함께 표시합니다. ")
            Text("이용자는 해당 신뢰도 정보를 참고하여 상황에 맞게 판단하실 수 있습니다.")
        }

        // 사용법
        Div({
            style {
                padding(14.px)
                borderRadius(14.px)
                backgroundColor(Color("#f8fafc"))
                border(1.px, LineStyle.Solid, Color("#e2e8f0"))
                marginBottom(12.px)
            }
        }) {
            P({
                style {
                    margin(0.px)
                    marginBottom(8.px)
                    fontSize(14.px)
                    fontWeight("700")
                    color(Color("#0f172a"))
                }
            }) { Text("사용 방법") }

            Ul({
                style {
                    margin(0.px)
                    paddingLeft(18.px)
                    color(Color("#374151"))
                    fontSize(14.px)
                    lineHeight("1.65")
                }
            }) {
                Li { Text("검색창에 음식 이름을 입력하세요 (예: 회, 커피, 팥, 참치).") }
                Li { Text("‘전체/안전/조건부/주의/금지’ 탭으로 출처 의견을 빠르게 필터링할 수 있어요.") }
                Li { Text("출처 링크(🔗)를 눌러 원문을 확인할 수 있어요.") }
                Li { Text("표시된 신뢰도 단계는 참고 지표로, 최종 판단은 개인의 상황에 따라 이루어져야 합니다.")}
            }
        }

//        // 예시 검색
//        Div({
//            style {
//                display(DisplayStyle.Flex)
//                flexWrap(FlexWrap.Wrap)
//                gap(8.px)
//                marginBottom(12.px)
//            }
//        }) {
//            ExampleChip("회")
//            ExampleChip("커피")
//            ExampleChip("팥")
//            ExampleChip("참치")
//            ExampleChip("치즈")
//        }

        // 면책 문구(메인 상단 디스클레이머와 톤 맞춤)
        Div({
            style {
                padding(12.px)
                borderRadius(12.px)
                backgroundColor(Color("#fff7ed"))
                border(1.px, LineStyle.Solid, Color("#fed7aa"))
            }
        }) {
            P({
                style {
                    margin(0.px)
                    fontSize(13.px)
                    color(Color("#7c2d12"))
                    lineHeight("1.6")
                }
            }) {
                Text("※ 본 서비스에서 제공하는 모든 정보는 일반적인 참고 목적이며, 의료·진단·치료를 위한 조언이 아닙니다.")
                Text("정보의 활용에 따른 최종 판단과 책임은 이용자에게 있으며, 필요 시 의료 전문가와 상담하시기 바랍니다.")
            }

            Div({
                style {
                    marginTop(10.px)
                    display(DisplayStyle.Flex)
                    gap(10.px)
                    flexWrap(FlexWrap.Wrap)
                }
            }) {
                A("/privacy.html", attrs = {
                    attr("target", "_blank")
                    attr("rel", "noopener noreferrer")
                    style { fontSize(13.px); color(Color("#9a3412")); textDecoration("underline") }
                }) { Text("개인정보처리방침") }

                A("/terms.html", attrs = {
                    attr("target", "_blank")
                    attr("rel", "noopener noreferrer")
                    style { fontSize(13.px); color(Color("#9a3412")); textDecoration("underline") }
                }) { Text("이용약관") }

                A("/disclaimer.html", attrs = {
                    attr("target", "_blank")
                    attr("rel", "noopener noreferrer")
                    style { fontSize(13.px); color(Color("#9a3412")); textDecoration("underline") }
                }) { Text("면책조항") }
            }
        }
    }
}

@Composable
private fun ExampleChip(text: String) {
    Span({
        style {
            padding(7.px, 10.px)
            borderRadius(999.px)
            backgroundColor(Color("#eef2ff"))
            border(1.px, LineStyle.Solid, Color("#e0e7ff"))
            color(Color("#3730a3"))
            fontSize(13.px)
            fontWeight("600")
        }
    }) { Text("예: $text") }
}